import React, { useEffect, useState } from "react";
import { MdModeEdit, MdOutlineDone } from "react-icons/md";
import { useSelector, useDispatch } from "react-redux";
import { FaArrowLeft } from "react-icons/fa";
import { RiAccountPinBoxFill } from "react-icons/ri";
import Heading from "../../utils/Heading.jsx";
import { motion } from "framer-motion";
import axios from "axios";
import { updateCustomer, fetchUserProfile } from "../../../../redux/reduxApi/SingIn&SingUp/updateCustomerApi";
import { toast } from "react-hot-toast";
import { useNavigate, Link } from "react-router-dom";

// Loader Component
const Loader = ({ size = "medium", color = "text-blue-600", message = "Loading..." }) => {
  const sizeClasses = {
    small: "w-4 h-4",
    medium: "w-8 h-8",
    large: "w-12 h-12"
  };

  const spinnerSize = sizeClasses[size] || sizeClasses.medium;

  return (
    <div className="flex flex-col items-center justify-center py-8">
      <div className={`${spinnerSize} border-4 border-gray-200 border-t-blue-600 rounded-full animate-spin ${color}`}></div>
      {message && <p className="mt-3 text-gray-600 text-sm">{message}</p>}
    </div>
  );
};

export default function CustomerAccountForm() {
  const [user, setUser] = useState({
    userId: "",
    UserEmail: "",
    UserName: "",
    UserFirstName: "",
    UserLastName: "",
    UserPhone: "",
  });

  const [editMode, setEditMode] = useState({
    UserFirstName: false,
    UserLastName: false,
    UserPhone: false,
    UserEmail: false,
  });

  const [inputValues, setInputValues] = useState({
    UserFirstName: "",
    UserLastName: "",
    UserPhone: "",
    UserEmail: "",
  });

  const [updating, setUpdating] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);

  // Get data from Redux store
  const profileData = useSelector((state) => state.updateCustomer?.profileData);
  const profileLoading = useSelector((state) => state.updateCustomer?.loading);
  const profileError = useSelector((state) => state.updateCustomer?.error);
  const updateResponse = useSelector((state) => state.updateCustomer?.customer);
  const updateLoading = useSelector((state) => state.updateCustomer?.loading);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Get userId from localStorage or profile data
  const getUserId = () => {
    // Try from localStorage
    const storedId = localStorage.getItem("UserRowId");
    if (storedId) return storedId;
    
    // Try from profile data
    if (profileData?.data?.webuser?.ROWID) 
      return profileData.data.webuser.ROWID;
    
    return null;
  };

  // Fetch profile on component mount
  useEffect(() => {
    const fetchData = async () => {
      const token = localStorage.getItem("authToken");
      if (token) {
        try {
          setIsLoading(true);
          setFetchError(null);
          console.log("Fetching user profile...");
          await dispatch(fetchUserProfile()).unwrap();
        } catch (error) {
          console.error("Error fetching profile:", error);
          setFetchError(error.message || "Failed to load profile data");
          toast.error("Failed to load profile data");
        } finally {
          setIsLoading(false);
        }
      } else {
        setIsLoading(false);
        toast.error("No authentication token found");
      }
    };

    fetchData();
  }, [dispatch]);

  // Update local state when profile data is received
  useEffect(() => {
    if (profileData?.data?.webuser) {
      const webuser = profileData.data.webuser;
      console.log("Profile data received:", webuser);
      
      const firstName = webuser.firstName || "";
      const lastName = webuser.lastName || "";
      const phone = webuser.phone || "";
      const email = webuser.email || "";
      const rowId = webuser.ROWID || "";

      // Store userId in localStorage for easy access
      if (rowId) {
        localStorage.setItem("UserRowId", rowId);
      }

      setUser({
        userId: rowId,
        UserEmail: email,
        UserFirstName: firstName,
        UserLastName: lastName,
        UserName: `${firstName} ${lastName}`.trim() || "User",
        UserPhone: phone,
      });

      setInputValues({
        UserFirstName: firstName,
        UserLastName: lastName,
        UserPhone: phone,
        UserEmail: email,
      });
    }
  }, [profileData]);

  // Handle successful update
  useEffect(() => {
    if (updateResponse?.success && !updateLoading) {
      console.log("Update successful, refreshing profile...");
      dispatch(fetchUserProfile());
      toast.success(updateResponse.message || "Profile updated successfully!");
      
      // Reset updating state
      setUpdating(false);
    }
  }, [updateResponse, updateLoading, dispatch]);

  const handleEditClick = (field) => {
    setEditMode((prev) => ({ ...prev, [field]: true }));
  };

  const handleDoneClick = async (field) => {
    const userId = getUserId();
    
    if (!userId) {
      toast.error("User ID not found. Please log in again.");
      return;
    }

    // Don't allow multiple updates at once
    if (updating) {
      toast.error("Update already in progress");
      return;
    }
    
    // Prepare update data with ALL fields (not just the one being edited)
    const updateData = {
      firstName: inputValues.UserFirstName,
      lastName: inputValues.UserLastName,
      phone: inputValues.UserPhone,
      email: inputValues.UserEmail
    };

    console.log(`Updating user with complete data:`, updateData);
    
    try {
      setUpdating(true);
      
      const response = await dispatch(
        updateCustomer({ 
          userId, 
          updateData 
        })
      ).unwrap();

      console.log("Update response:", response);
      
      if (response.success) {
        // Close edit mode for this field
        setEditMode((prev) => ({ ...prev, [field]: false }));
        
        // Update localStorage with basic info
        localStorage.setItem("userEmail", inputValues.UserEmail);
        
        // Success message will be shown by the useEffect above
      } else {
        toast.error(response.message || "Failed to update");
        setUpdating(false);
      }
    } catch (error) {
      console.error("Update error:", error);
      toast.error(typeof error === 'string' ? error : "Update failed");
      setUpdating(false);
    }
  };

  const handleInputChange = (field, value) => {
    setInputValues((prev) => ({ ...prev, [field]: value }));
  };

  const text = "Account Details";

  const letterVariants = {
    hidden: { opacity: 0, x: -10 },
    visible: (i) => ({
      opacity: 1,
      x: 0,
      transition: {
        delay: i * 0.05,
      },
    }),
  };

  // Show loader while fetching initial data
  if (isLoading) {
    return (
      <div className="w-full min-h-[400px] flex items-center justify-center">
        <div className="text-center">
          <Loader size="large" message="Loading your profile..." />
          <p className="text-gray-500 text-sm mt-2">Please wait while we fetch your account details</p>
        </div>
      </div>
    );
  }

  // Show error message if fetch failed
  if (fetchError) {
    return (
      <div className="w-full min-h-[400px] flex items-center justify-center">
        <div className="text-center p-6 bg-red-50 rounded-lg">
          <p className="text-red-600 mb-4">Error: {fetchError}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="w-full md:pl-2 md:mt-0 mx-auto flex-wrap my-auto py-[0px] mb-0 md:!mb-60 lg:!mb-0">
        <div className="relative flex items-center justify-center mt-2 lg:mt-0 mb-1">
          <Link to="/account/dashboard" className="absolute left-0">
            <FaArrowLeft size="20px" />
          </Link>

          <div className="text-center">
            <Heading text={<span className="inline-flex items-center gap-2">Account Details <RiAccountPinBoxFill className="text-[#661e33] lg:text-5xl md:text-4xl text-3xl inline-block" /></span>} />
            <hr className="heading-seperator border-t-4 border-[#66944b] opacity-100 w-24 h-4 mx-auto" />
          </div>
        </div>

        <div className="w-full">
          <form className="space-y-4" onSubmit={(e) => e.preventDefault()}>
            {/* First Name */}
            <div className="relative">
              <label className="block text-sm font-medium mb-1" htmlFor="first-name">
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                className="w-full border border-gray-300 p-2 rounded"
                id="first-name"
                type="text"
                value={inputValues.UserFirstName}
                disabled={!editMode.UserFirstName || updating}
                onChange={(e) => handleInputChange("UserFirstName", e.target.value)}
              />
              {!editMode.UserFirstName ? (
                <MdModeEdit 
                  className="absolute right-2 top-9 cursor-pointer" 
                  size={20} 
                  onClick={() => handleEditClick("UserFirstName")} 
                />
              ) : (
                <MdOutlineDone 
                  className={`absolute right-2 top-9 cursor-pointer ${updating ? 'text-gray-400' : 'text-green-600'}`} 
                  size={20} 
                  onClick={() => !updating && handleDoneClick("UserFirstName")} 
                />
              )}
            </div>

            {/* Last Name */}
            <div className="relative">
              <label className="block text-sm font-medium mb-1" htmlFor="last-name">
                Last Name <span className="text-red-500">*</span>
              </label>
              <input
                className="w-full border border-gray-300 p-2 rounded"
                id="last-name"
                type="text"
                value={inputValues.UserLastName}
                disabled={!editMode.UserLastName || updating}
                onChange={(e) => handleInputChange("UserLastName", e.target.value)}
              />
              {!editMode.UserLastName ? (
                <MdModeEdit 
                  className="absolute right-2 top-9 cursor-pointer" 
                  size={20} 
                  onClick={() => handleEditClick("UserLastName")} 
                />
              ) : (
                <MdOutlineDone 
                  className={`absolute right-2 top-9 cursor-pointer ${updating ? 'text-gray-400' : 'text-green-600'}`} 
                  size={20} 
                  onClick={() => !updating && handleDoneClick("UserLastName")} 
                />
              )}
            </div>

            {/* Mobile Number */}
            <div className="relative">
              <label className="block text-sm font-medium mb-1" htmlFor="mobile-number">
                Mobile Number <span className="text-red-500">*</span>
              </label>
              <input
                className="w-full border border-gray-300 p-2 rounded"
                id="mobile-number"
                type="text"
                value={inputValues.UserPhone}
                disabled={!editMode.UserPhone || updating}
                onChange={(e) => handleInputChange("UserPhone", e.target.value)}
              />
              {!editMode.UserPhone ? (
                <MdModeEdit 
                  className="absolute right-2 top-9 cursor-pointer" 
                  size={20} 
                  onClick={() => handleEditClick("UserPhone")} 
                />
              ) : (
                <MdOutlineDone 
                  className={`absolute right-2 top-9 cursor-pointer ${updating ? 'text-gray-400' : 'text-green-600'}`} 
                  size={20} 
                  onClick={() => !updating && handleDoneClick("UserPhone")} 
                />
              )}
            </div>

            {/* Email Address */}
            <div className="relative">
              <label className="block text-sm font-medium mb-1" htmlFor="email-address">
                Email address <span className="text-red-500">*</span>
              </label>
              <input
                className="w-full border border-gray-300 p-2 rounded"
                id="email-address"
                type="email"
                value={inputValues.UserEmail}
                disabled={!editMode.UserEmail || updating}
                onChange={(e) => handleInputChange("UserEmail", e.target.value)}
              />
              {!editMode.UserEmail ? (
                <MdModeEdit 
                  className="absolute right-2 top-9 cursor-pointer" 
                  size={20} 
                  onClick={() => handleEditClick("UserEmail")} 
                />
              ) : (
                <MdOutlineDone 
                  className={`absolute right-2 top-9 cursor-pointer ${updating ? 'text-gray-400' : 'text-green-600'}`} 
                  size={20} 
                  onClick={() => !updating && handleDoneClick("UserEmail")} 
                />
              )}
            </div>
            
            {/* Update loader - shows while updating profile */}
            {updating && (
              <div className="flex items-center justify-center gap-2 mt-4">
                <Loader size="small" message="Updating profile..." />
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}
